import { AuthUser } from '@/types/auth';
import { Provider } from '@/types/tenant';

function readString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return value.toString();
  }

  return undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }

  return undefined;
}

export function coerceAuthUser(candidate: unknown): AuthUser | null {
  if (typeof candidate !== 'object' || candidate === null) {
    return null;
  }

  const record = candidate as Record<string, unknown>;
  const id = readString(record.id);
  const email = readString(record.email);
  const name = typeof record.name === 'string' ? record.name : email;

  if (!id || !email || !name) {
    return null;
  }

  const providerIdValue = readString(record.provider_id ?? record.providerId);
  const hasPasswordValue = readBoolean(record.has_password ?? record.hasPassword);
  const roleValue = readString(record.role)?.trim();

  return {
    id,
    email,
    name,
    role: roleValue ? (roleValue as AuthUser['role']) : undefined,
    provider_id: providerIdValue,
    hasPassword: hasPasswordValue,
    created_at: typeof record.created_at === 'string' ? record.created_at : (record.createdAt as string | undefined),
    updated_at: typeof record.updated_at === 'string' ? record.updated_at : (record.updatedAt as string | undefined),
    avatar: typeof record.avatar === 'string' ? record.avatar : undefined,
    roles: Array.isArray(record.roles) ? (record.roles as string[]) : undefined,
    permissions: Array.isArray(record.permissions) ? (record.permissions as string[]) : undefined,
  };
}

export function coerceProvider(candidate: unknown): Provider | null {
  if (typeof candidate !== 'object' || candidate === null) {
    return null;
  }

  const record = candidate as Record<string, unknown>;
  const id = readString(record.id);
  const name = typeof record.name === 'string' ? record.name : undefined;
  const slug = typeof record.slug === 'string' ? record.slug : undefined;
  const domain = typeof record.domain === 'string' ? record.domain : undefined;

  if (!id || !name || !slug || !domain) {
    return null;
  }

  const createdAt =
    typeof record.created_at === 'string'
      ? record.created_at
      : (record.createdAt as string | undefined) ?? new Date().toISOString();
  const updatedAt =
    typeof record.updated_at === 'string'
      ? record.updated_at
      : (record.updatedAt as string | undefined) ?? createdAt;

  return {
    id,
    name,
    slug,
    domain,
    customDomains: Array.isArray(record.customDomains) ? (record.customDomains as string[]) : [],
    createdAt,
    updatedAt,
  };
}
